import { CademyError } from '@shared/domain-shared';
import { OriginString, PaymentMethod } from '@shared/types';
import { QueryFunction, UseQueryOptions, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

type CostSummaryArguments = {
    courseId: string;
    occurrenceId?: string;
    voucherId?: string;
    offers: Array<{ offerId: string; quantity: number }>;
    addons: Array<{ addonId: string; quantity: number }>;
    origin: OriginString;
    paymentType?: PaymentMethod;
    extras?: Array<{ label: string; cost: number; quantity: number }>;
};

export type SummaryPart = { text: string; value: number };

export type CostSummary = {
    total_cost: number;
    cademy_fees: number;
    cademy_vat: number;
    vat_amount: number;
    vat_enabled: boolean;
    vat_absorbed: boolean;
    fees_absorbed: boolean;
    marketplace_fees: number;
    paid_by_giftcard: number;
    paid_by_promotion: number;
    discount: unknown;
    summary_items: {
        addOnSummary: Array<SummaryPart>;
        discountSummary: Array<SummaryPart>;
        extraSummary: Array<SummaryPart>;
        offerSummary: Array<SummaryPart>;
        vatSummary: Array<SummaryPart>;
        voucherSummary: Array<SummaryPart>;
    };
};

const getCostSummary = async ({
    courseId,
    occurrenceId,
    voucherId,
    offers,
    addons,
    origin,
    paymentType,
    extras,
}: CostSummaryArguments): Promise<CostSummary> => {
    const payload = {
        course_ref: courseId,
        occurrence_ref: occurrenceId,
        voucher_ref: voucherId,
        offers: offers.map((offer) => {
            return { _id: offer.offerId, quantity: offer.quantity };
        }),
        add_ons: addons.map((addon) => {
            return { _id: addon.addonId, quantity: addon.quantity };
        }),
        origin,
        payment_method: paymentType,
        extras,
    };
    const response = await fetch(
        `${process.env.NEXT_PUBLIC_CADEMY_API_URL}/custom/get-checkout-summary`,
        {
            method: 'POST',
            body: JSON.stringify(payload),
            credentials: 'include',
        }
    );

    if (!response.ok) {
        if (response.headers.get('content-type') === 'application/problem+json') {
            const problemDetails = await response.json();
            const error = CademyError.fromObject(problemDetails);
            throw error;
        }
        const error = await response.text();
        throw new Error(error);
    }

    return await response.json();
};

type QueryKey = [
    queryType: 'cost-summary',
    courseId: string,
    occurrenceId: string | undefined,
    voucherId: string | undefined,
    offers: Array<{ offerId: string; quantity: number }>,
    addons: Array<{ addonId: string; quantity: number }>,
    origin: OriginString,
    paymentType: PaymentMethod | undefined,
    extras: Array<{ label: string; cost: number; quantity: number }> | undefined,
];
const costSummaryQueryFunction: QueryFunction<CostSummary, QueryKey> = ({ queryKey }) => {
    const summaryArguments: CostSummaryArguments = {
        courseId: queryKey[1],
        occurrenceId: queryKey[2],
        voucherId: queryKey[3],
        offers: queryKey[4],
        addons: queryKey[5],
        origin: queryKey[6],
        paymentType: queryKey[7],
        extras: queryKey[8],
    };
    return getCostSummary(summaryArguments);
};

export type UseCostSummaryOptions = Omit<
    UseQueryOptions<CostSummary, unknown, CostSummary, QueryKey>,
    'queryKey' | 'queryFn'
>;
export const useCostSummary = (
    costSummaryArgs: CostSummaryArguments,
    options?: UseCostSummaryOptions
) => {
    const queryKey = useMemo((): QueryKey => {
        return [
            'cost-summary',
            costSummaryArgs.courseId,
            costSummaryArgs.occurrenceId,
            costSummaryArgs.voucherId,
            costSummaryArgs.offers,
            costSummaryArgs.addons,
            costSummaryArgs.origin,
            costSummaryArgs.paymentType,
            costSummaryArgs.extras,
        ];
    }, [costSummaryArgs]);
    return useQuery({
        queryKey,
        queryFn: costSummaryQueryFunction,
        ...options,
    });
};
